import { useEffect, useState } from 'react';
import { Box, Button, Card, List, MenuItem, Stack, TextField, useMediaQuery, type Theme } from '@mui/material';
import { useParams } from 'react-router-dom';
import AddCircleOutline from '@mui/icons-material/AddCircleOutlined';
import { EmptyState } from '../components/EmptyState';
import { FilterBar, FilterSelect } from '../components/ListControls';
import { TabStrip } from '../components/TabStrip';
import { TitleBar } from '../components/TitleBar';
import { GiveTaskDrawer } from '../features/approvals/GiveTaskDrawer';
import { ApprovalListItem } from '../features/approvals/ApprovalListItem';
import { ProposalCard } from '../features/approvals/ProposalCard';
import {
  KIND_LABEL,
  STATUS_LABEL,
  URGENCIES,
  type ApprovalKind,
  type ApprovalStatus,
} from '../features/approvals/sample';
import { useApprovalFilters } from '../features/approvals/useApprovalFilters';
import { useApprovals } from '../features/approvals/useApprovals';

const STATUSES = Object.keys(STATUS_LABEL) as ApprovalStatus[];

/** Claude's proposals, one card each: read what Claude wrote, tell it what to change, approve or reject. Nothing is sent by approving. */
export function ApprovalsPage() {
  const { id } = useParams();
  const { items, decide, edit, instruct, loading } = useApprovals();
  const f = useApprovalFilters(items);
  const [task, setTask] = useState(false);
  const target = items.find((a) => a.id === id);
  const phone = useMediaQuery((t: Theme) => t.breakpoints.down('md'));
  const [picked, setPicked] = useState<string>();
  const selectedId = picked ?? id;
  // on a phone the list and the card take turns; on a bigger screen they sit side by side
  const [closed, setClosed] = useState(false);
  const open = !closed && Boolean(selectedId);

  // a link to one proposal (from a notification) opens its tab and brings it into view
  useEffect(() => {
    if (!target) return;
    f.setStatus(target.status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);

  const current = f.shown.find((a) => a.id === selectedId) ?? (phone ? undefined : f.shown[0]);

  return (
    <Stack spacing={2}>
      <TitleBar
        title="Approvals"
        subtitle="Review and approve Claude's proposals. You can also give instructions or create a new task for Claude."
      >
        <Button variant="outlined" startIcon={<AddCircleOutline />} onClick={() => setTask(true)}>
          Give Claude a Task
        </Button>
      </TitleBar>

      <TabStrip
        tabs={STATUSES.map((s) => ({
          value: s,
          label: STATUS_LABEL[s],
          count: items.filter((a) => a.status === s).length,
        }))}
        value={f.status}
        onChange={f.setStatus}
      />
      <FilterBar placeholder="Search approvals…" search={f.search} onSearch={f.setSearch}>
        <FilterSelect
          label="Type"
          value={f.kind}
          onChange={f.setKind}
          options={Object.keys(KIND_LABEL) as ApprovalKind[]}
        />
        <FilterSelect label="Urgency" value={f.urgency} onChange={f.setUrgency} options={URGENCIES} />
        <TextField select label="Sort" value={f.sort} onChange={(e) => f.setSort(e.target.value)} sx={{ width: 150 }}>
          <MenuItem value="urgent">Urgent first</MenuItem>
          <MenuItem value="newest">Newest first</MenuItem>
        </TextField>
      </FilterBar>

      {f.shown.length === 0 && <EmptyState>{loading ? 'Loading…' : 'Nothing here.'}</EmptyState>}
      {f.shown.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            alignItems: 'start',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '340px minmax(0, 1fr)' },
          }}
        >
          {(!phone || !open || !current) && (
            <Card
              sx={{ overflow: 'auto', maxHeight: { md: 'calc(100vh - 280px)' }, position: { md: 'sticky' }, top: 80 }}
            >
              <List disablePadding>
                {f.shown.map((a) => (
                  <ApprovalListItem
                    key={a.id}
                    a={a}
                    selected={a.id === current?.id}
                    onSelect={(next) => {
                      setPicked(next);
                      setClosed(false);
                    }}
                  />
                ))}
              </List>
            </Card>
          )}
          {current && (!phone || open) && (
            <ProposalCard
              key={current.id}
              a={current}
              onDecide={decide}
              onEdit={edit}
              onInstruct={instruct}
              onBack={phone ? () => setClosed(true) : undefined}
            />
          )}
        </Box>
      )}

      <GiveTaskDrawer open={task} onClose={() => setTask(false)} />
    </Stack>
  );
}
